/**
 * NULL-aware sums and ratios for /channels (spec §3.3, §3.4; R14). A metric no
 * row reported stays null, never 0. A tile ratio divides sums taken over the
 * rows that report its bracketed metric, so a day without video views adds
 * neither spend nor views to CPV. A ratio over a null or 0 denominator is null
 * (rendered "—"). Box 5 keeps D1's CTR / CPC / CPM over plain sums, so its
 * total row divides exactly as box 2 does, and D1's low-sample rule. Pure.
 *
 * @module services/channels/channelMath
 */
import { addNullable, cpcOf, cpmOf, ctrOf, isLowSample, ratioOf } from '@/services/overview/overviewMath';
import type { Totals } from '@/services/overview/overviewTypes';
import type { BreakdownMetrics, ChannelAdRow } from './channelTypes';

/** Σ value over the rows that report it; null when none does. A reported 0 stays 0. */
export const sumOf = <R>(rows: readonly R[], value: (r: R) => number | null): number | null =>
  rows.reduce<number | null>((sum, r) => addNullable(sum, value(r)), null);

/** The rows whose value is a number (0 included), in order. */
export const reportingRows = <R>(rows: readonly R[], value: (r: R) => number | null): R[] =>
  rows.filter((r) => value(r) !== null);

/**
 * Σnum × scale ÷ Σden, both summed over the rows that report `reportedBy` (R14):
 * CPV = spend ÷ video views over the rows with video views; ROAS = conversion
 * value ÷ spend over the rows with a conversion value. null when Σden is null or
 * 0, and when Σnum is null.
 */
export const ratioOverReporting = <R>(
  rows: readonly R[],
  num: (r: R) => number | null,
  den: (r: R) => number | null,
  reportedBy: (r: R) => number | null,
  scale = 1,
): number | null => {
  const reporting = reportingRows(rows, reportedBy);
  return ratioOf(sumOf(reporting, num), sumOf(reporting, den), scale);
};

/** What a box 5 row sums: ad rows (Ad format, Creative) or fact rows (Campaign, Targeting). */
export type MetricSource = Pick<ChannelAdRow, 'spend' | 'impressions' | 'clicks' | 'reactions' | 'comments' | 'shares'>;

/** Box 5 columns over `rows` (spec §3.4): NULL-aware sums, then D1's CTR / CPC / CPM over those sums. */
export const breakdownMetricsOf = (rows: readonly MetricSource[]): BreakdownMetrics => {
  const spend = sumOf(rows, (r) => r.spend);
  const impressions = sumOf(rows, (r) => r.impressions);
  const clicks = sumOf(rows, (r) => r.clicks);
  // D1's helpers take Totals; they never read the three fields left null here.
  const totals: Totals = { spend, impressions, clicks, conversions: null, conversionValue: null, reach: null };
  return {
    spend,
    impressions,
    clicks,
    ctr: ctrOf(totals),
    cpc: cpcOf(totals),
    cpm: cpmOf(totals),
    reactions: sumOf(rows, (r) => r.reactions),
    comments: sumOf(rows, (r) => r.comments),
    shares: sumOf(rows, (r) => r.shares),
    // D1 R17: impressions known and < 500 (BENCHMARK_MIN_IMPRESSIONS); unreported impressions is not a small sample.
    lowSample: isLowSample(totals),
  };
};

/** A sort: the biggest spend first, NULL spend after every number (0 included), ties by label. */
export const bySpendDesc = <R extends { spend: number | null }>(label: (r: R) => string): ((a: R, b: R) => number) =>
  (a, b) => {
    if (a.spend !== b.spend) {
      if (a.spend === null) return 1;
      if (b.spend === null) return -1;
      return b.spend - a.spend;
    }
    return label(a).localeCompare(label(b));
  };
